import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, ArrowLeft } from "lucide-react";
import { WEDDING } from "@/lib/constants";

const NAV_ITEMS = [
  { label: "Home", href: "#home" },
  { label: "Il Giorno", href: "#giorno" },
  { label: "Info Utili", href: "#info" },
  { label: "RSVP", href: "#rsvp" },
  { label: "Domande Frequenti", href: "#domande" },
];

export default function StickyNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );
    NAV_ITEMS.forEach(item => {
      const el = document.querySelector(item.href);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? 'glass-nav shadow-sm' : 'bg-transparent'
      }`}
      data-testid="sticky-nav"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-14">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            data-testid="nav-back"
            className={`flex items-center justify-center w-10 h-10 -ml-2 rounded-full transition-colors ${
              scrolled ? 'text-w-muted hover:text-w-charcoal hover:bg-w-sand/50' : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className={`font-heading text-lg hidden sm:inline transition-colors duration-300 ${
            scrolled ? 'text-w-charcoal' : 'text-white'
          }`}>
            {WEDDING.sposa} <span className="font-accent italic text-w-gold text-sm">&</span> {WEDDING.sposo}
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-5">
          {NAV_ITEMS.map(item => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`font-body text-[15px] uppercase tracking-widest transition-colors duration-200 ${
                activeSection === item.href.slice(1) ? 'text-w-sage' : 'text-w-muted hover:text-w-charcoal'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo('#rsvp')}
            data-testid="nav-cta-rsvp"
            className="bg-w-sage text-white font-body text-[10px] uppercase tracking-widest px-4 py-2 rounded-full hover:bg-w-sage/90 transition-colors btn-wedding"
          >
            Dicci se ci sarai
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden w-8 h-8 flex items-center justify-center text-w-charcoal"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass-nav border-t border-w-sand/50 px-4 pb-4 pt-2">
          {NAV_ITEMS.map(item => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className={`block w-full text-left py-2.5 font-body text-sm transition-colors ${
                activeSection === item.href.slice(1) ? 'text-w-sage' : 'text-w-muted'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}
